import type { TFunction } from 'i18next';
import type { AppLocale } from '../i18n';
import type { SubscriptionActivityItem } from '../types/subscription';
import { formatAppDate, formatAppDateTime } from './localeDateFormat';
import { toPersianDigits } from './persianUtils';

function formatActivityNumber(value: number, locale: AppLocale): string {
  const formatted = value.toLocaleString(locale === 'fa' ? 'fa-IR' : 'en-US');
  return locale === 'fa' ? toPersianDigits(formatted) : formatted;
}

function formatActivityCurrency(amount: number, currency: string, locale: AppLocale): string {
  try {
    const formatted = new Intl.NumberFormat(locale === 'fa' ? 'fa-IR' : 'en-US', {
      style: 'currency',
      currency,
    }).format(amount);
    return locale === 'fa' ? toPersianDigits(formatted) : formatted;
  } catch {
    return `${formatActivityNumber(amount, locale)} ${currency}`;
  }
}

function formatActivityShortDate(value: Date | string | number, locale: AppLocale): string {
  return formatAppDate(value, locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

const ACTIVITY_DATE_RANGE_SEP = /\s*[–—-]\s*/;

function parseActivityDate(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseActivityDateRangeDetail(detail: string): { start: Date; end: Date } | null {
  const parts = detail.trim().split(ACTIVITY_DATE_RANGE_SEP);
  if (parts.length !== 2) return null;
  const start = parseActivityDate(parts[0]);
  const end = parseActivityDate(parts[1]);
  if (!start || !end) return null;
  return { start, end };
}

function resolveSubscriptionActivityDateRange(
  item: SubscriptionActivityItem
): { start: Date | string; end: Date | string } | null {
  if (item.subscriptionStartDate && item.subscriptionEndDate) {
    return { start: item.subscriptionStartDate, end: item.subscriptionEndDate };
  }
  if (item.detail) {
    const parsed = parseActivityDateRangeDetail(item.detail);
    if (parsed) return parsed;
  }
  return null;
}

function formatActivityDateRange(
  range: { start: Date | string; end: Date | string },
  t: TFunction<'subscription'>,
  locale: AppLocale
): string {
  return t('activityDetailDateRange', {
    start: formatActivityShortDate(range.start, locale),
    end: formatActivityShortDate(range.end, locale),
  });
}

function inferSubscriptionActive(item: SubscriptionActivityItem): boolean {
  if (item.isSubscriptionActive != null) {
    return item.isSubscriptionActive;
  }
  if (item.title === 'Subscription period') return true;
  if (item.title === 'Subscription record') return false;
  if (item.subscriptionEndDate) {
    return new Date(item.subscriptionEndDate).getTime() > Date.now();
  }
  return false;
}

export function getSubscriptionActivityTitle(
  item: SubscriptionActivityItem,
  t: TFunction<'subscription'>
): string {
  switch (item.activityType) {
    case 'xp_redeem':
      return t('activityTitleXpRedeem');
    case 'purchase':
      return t('activityTitlePurchase');
    case 'admin_assign':
      return t('activityTitleAdminAssign');
    case 'subscription':
      return inferSubscriptionActive(item)
        ? t('activityTitleSubscriptionPeriod')
        : t('activityTitleSubscriptionRecord');
    default:
      return item.title;
  }
}

export function getSubscriptionActivityDetail(
  item: SubscriptionActivityItem,
  t: TFunction<'subscription'>,
  locale: AppLocale
): string | undefined {
  switch (item.activityType) {
    case 'xp_redeem': {
      const days = item.grantDurationDays ?? 0;
      const xp = formatActivityNumber(item.xpSpent ?? 0, locale);
      let daysLabel = t('activityDetailGrantDays', { count: days });
      if (locale === 'fa') {
        daysLabel = toPersianDigits(daysLabel);
      }
      if (item.offerName?.trim()) {
        return t('activityDetailXpSpentWithOffer', { offer: item.offerName, xp, days: daysLabel });
      }
      return t('activityDetailXpSpent', { xp, days: daysLabel });
    }
    case 'purchase': {
      const amount = formatActivityCurrency(item.amount ?? 0, item.currency ?? 'EUR', locale);
      return t('activityDetailPaid', { amount });
    }
    case 'admin_assign':
      return t('activityDetailAdminAssign');
    case 'subscription': {
      const range = resolveSubscriptionActivityDateRange(item);
      if (range) {
        return formatActivityDateRange(range, t, locale);
      }
      return item.detail;
    }
    default:
      return item.detail;
  }
}

export function formatSubscriptionActivityOccurredAt(value: string, locale: AppLocale): string {
  return formatAppDateTime(value, locale);
}
